<template>
  <section class="bg-[#fef9f6] pt-16 pb-24">
    <!-- Header -->
    <div class="max-w-6xl mx-auto px-6 text-center mb-12">
      <h1 class="text-3xl md:text-4xl font-extrabold text-[#924f19]">
        <span class="block">Magnum Dental Clinic</span>
        <span class="block">Trusted by Patients Across Guwahati</span>
      </h1>

      <p class="mt-3 text-gray-600 max-w-2xl mx-auto">
        Real Patient Reviews and Experiences at Magnum Dental Clinic
      </p>
    </div>

    <!-- Rating Summary -->
    <div class="max-w-4xl mx-auto px-6 mb-16">
      <div class="bg-[#f3fbfb] rounded-3xl px-8 py-10
               flex flex-col sm:flex-row items-center
               gap-6 sm:gap-10 text-center sm:text-left">
        <!-- Stars & Rating -->
        <div>
          <div class="flex items-center justify-center sm:justify-start gap-2 mb-2">
            <span class="text-lg font-semibold text-gray-800">
              Rated 5/5
            </span>
            <div class="text-yellow-400 text-lg">
              ★★★★★
            </div>
          </div>

          <p class="text-4xl sm:text-5xl font-extrabold text-[#f47b20] leading-tight">
            {{ googleReviews.length }}+
          </p>
          <p class="text-xl font-bold text-gray-800">
            Happy Reviews
          </p>

          <div class="mt-2 flex items-center justify-center sm:justify-start gap-2">
            <img src="/google-colored.svg" alt="Google Verified" class="w-6 h-6" />
            <span class="text-gray-600 font-medium">
              Google Verified
            </span>
          </div>
        </div>

        <!-- Supporting text -->
        <div class="max-w-md">
          <p class="text-gray-600 text-base leading-relaxed">
            At Magnum Dental Clinic Guwahati, patient satisfaction is at the heart of everything we do. Discover real
            stories, genuine experiences, and honest feedback from patients who trust us for advanced, comfortable, and
            personalized dental care in Guwahati.
            These reviews reflect our commitment to excellence, transparency, and long-lasting smile results.
          </p>
        </div>
      </div>
    </div>

    <!-- GOOGLE REVIEWS -->
    <div class="max-w-6xl mx-auto px-6 overflow-hidden">
      <div class="flex transition-transform duration-300 ease-in-out cursor-grab active:cursor-grabbing"
        :style="{ transform: `translateX(-${currentIndex * slideWidth}%)` }" @mousedown="startDrag" @mousemove="onDrag"
        @mouseup="endDrag" @mouseleave="endDrag" @touchstart.passive="onTouchStart" @touchend="onTouchEnd">


        <div v-for="review in googleReviews" :key="review.name"
          class="min-w-full sm:min-w-[50%] lg:min-w-[33.333%] px-4">
          <div class="relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition
         h-[260px] flex flex-col cursor-pointer" @click="onReviewClick()">

            <!-- Google Icon -->
            <img src="/google-colored.svg" alt="Google Review" class="absolute top-5 right-5 w-5 h-5 opacity-80" />

            <div class="flex items-center gap-3 mb-3">
              <div class="w-10 h-10 rounded-full bg-[#924f19]/10
                   flex items-center justify-center
                   font-bold text-[#924f19]">
                {{ review.name.charAt(0) }}
              </div>
              <div>
                <p class="font-semibold text-gray-800">
                  {{ review.name }}
                </p>
                <p class="text-xs text-gray-400">
                  {{ review.date }}
                </p>
              </div>
            </div>

            <div class="text-yellow-400 mb-3">★★★★★</div>

            <p class="text-gray-600 text-sm leading-relaxed
         max-h-40 overflow-y-auto pr-1" @scroll="onInnerScroll">
              {{ review.text }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- OWNER MESSAGE -->
    <div class="max-w-4xl mx-auto px-6 mt-20 text-center">
      <div class="bg-white rounded-3xl p-8 md:p-10 shadow-sm">
        <p class="text-lg text-gray-700 leading-relaxed font-medium">
          Every smile we treat and every review you share means the world to us.
          Your kind words and continued support inspire us to give our best every
          single day. Thank you for being a part of our Magnum Dental family!
        </p>

        <p class="mt-4 text-sm text-gray-500 italic">
          — With gratitude, the Magnum Dental Team
        </p>
      </div>
    </div>

    <!-- CTA -->
    <div class="mt-24 text-center">
      <h3 class="text-2xl font-bold text-[#924f19] mb-4">
        Ready to start your smile journey?
      </h3>
      <router-link to="/book-appointment-dental-clinic-guwahati" class="inline-block bg-[#924f19] hover:bg-[#7b4215]
               text-white px-8 py-3 rounded-full font-semibold transition">
        Book Appointment
      </router-link>
    </div>
  </section>
</template>

<script>
import { useHead } from "@vueuse/head";
import googleReviewsData from "@/data/reviews.json";

export default {
  name: 'PatientReviews',
  setup() {
    useHead({
      title: "Patient Reviews | Magnum Dental Clinic Guwahati",
      meta: [
        {
          name: "description",
          content:
            "Read real patient reviews from Magnum Dental Clinic Guwahati. Trusted by hundreds of patients for dental implants, root canal, braces, and more.",
        },
        {
          name: "robots",
          content: "index, follow",
        },
        {
          property: "og:title",
          content: "Patient Reviews | Magnum Dental Clinic Guwahati",
        },
        {
          property: "og:url",
          content: "https://www.magnumdentalguwahati.in/patient-reviews-magnum-dental",
        },
      ],
      link: [
        { rel: "canonical", href: "https://www.magnumdentalguwahati.in/patient-reviews-magnum-dental" },
      ],
    });
  },
  data() {
    return {
      currentIndex: 0,
      slideWidth: 100,
      interval: null,
      dragStartX: 0,
      dragStartY: 0,
      isDragging: false,
      resumeTimeout: null,
      isScrolling: false,
      scrollTimeout: null,
      googleReviews: googleReviewsData
    };
  },
  mounted() {
    this.updateSlideWidth();
    window.addEventListener('resize', this.updateSlideWidth);

    this.startAutoSlide();
  },
  beforeUnmount() {
    this.stopAutoSlide();
    window.removeEventListener('resize', this.updateSlideWidth);
    clearTimeout(this.resumeTimeout);
    clearTimeout(this.scrollTimeout);
  },
  methods: {
    startAutoSlide() {
      if (this.interval) clearInterval(this.interval);
      this.interval = setInterval(this.nextSlide, 2500);
    },
    stopAutoSlide() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    nextSlide() {
      const visibleSlides = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1;
      const maxIndex = this.googleReviews.length - visibleSlides;

      this.currentIndex =
        this.currentIndex >= maxIndex ? 0 : this.currentIndex + 1;
    },
    updateSlideWidth() {
      if (window.innerWidth >= 1024) this.slideWidth = 33.333;
      else if (window.innerWidth >= 640) this.slideWidth = 50;
      else this.slideWidth = 100;
    },
    scheduleResume() {
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => {
        this.startAutoSlide();
      }, 3000); // resume after 3s of inactivity
    },
    startDrag(e) {
      if (e.touches) return; // Let mobile use touch handlers only

      this.stopAutoSlide();
      clearTimeout(this.resumeTimeout);

      this.isDragging = true;
      this.dragStartX = e.clientX;
    },
    onDrag(e) {
      if (!this.isDragging) return;

      const diff = this.dragStartX - e.clientX;

      if (Math.abs(diff) > 60) {
        diff > 0 ? this.nextSlide() : this.prevSlide();
        this.isDragging = false;
        this.scheduleResume();
      }
    },
    endDrag() {
      if (!this.isDragging && !this.isScrolling) return;

      this.isDragging = false;

      // If user was scrolling, wait longer before resuming auto-slide
      if (this.isScrolling) {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.startAutoSlide();
        }, 2000);
      } else {
        this.scheduleResume();
      }
    },
    onReviewClick() {
      // Stop auto-slide when user clicks on a review card
      this.stopAutoSlide();
      clearTimeout(this.resumeTimeout);
      this.scheduleResume();
    },
    prevSlide() {
      this.currentIndex =
        this.currentIndex === 0 ? this.currentIndex : this.currentIndex - 1;
    },
    onTouchStart() {
      this.stopAutoSlide();
      clearTimeout(this.resumeTimeout);
    },

    onTouchEnd() {
      this.scheduleResume();
    },

    onInnerScroll() {
      this.stopAutoSlide();
      clearTimeout(this.resumeTimeout);
      this.scheduleResume();
    },

  }
};

</script>
